import { formatNullableSpend, formatPercent } from "./format-opportunity"

export interface DualSpendDisplay {
  /** Share of the entity's total creatives — the number findings gaps are measured in. */
  primary: string
  /** Pre-formatted percent string for the share within the angle bucket (without label). */
  secondaryValue: string | null
  /** Value for bar normalization — total creatives, so bars compare like with like. */
  barValue: number | null
  unavailable: boolean
}

/** Resolve primary (total creatives) and secondary (within the angle) spend display. */
export function getDualSpendDisplay(
  perDataset: number | null,
  totalCreatives: number | null,
  decimals = 1,
): DualSpendDisplay {
  // Per-angle shares the total's numerator but has the smaller denominator, so a
  // valid total is always <= per-angle and never above 100%. A total that breaks
  // either rule comes from mismatched sources and is shown as unavailable rather
  // than as a misleading headline (e.g. 102%).
  const total = isConsistentTotal(totalCreatives, perDataset) ? totalCreatives : null

  return {
    primary: formatNullableSpend(total, decimals),
    secondaryValue: perDataset === null ? null : formatPercent(perDataset, decimals),
    barValue: total,
    unavailable: total === null,
  }
}

function isConsistentTotal(total: number | null, perDataset: number | null): total is number {
  if (total === null || total > 1) return false
  return perDataset === null || total <= perDataset
}

/** Sub-feature value that stands for the whole feature; it is not shown. */
const MAIN_SUB_FEATURE = "Main"

/**
 * The feature + sub-feature bucket the secondary spend share is measured in —
 * "Narrative › Painpoint", or just "Concept" when the sub-feature is Main.
 */
export function formatSpendBucket(feature?: string | null, subFeature?: string | null): string | null {
  if (!feature) return null
  return subFeature && subFeature !== MAIN_SUB_FEATURE ? `${feature} › ${subFeature}` : feature
}
